/**
 * ApplicationMethod Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { toast } from 'sonner';
import { useApplicationMethodStore } from '@/store/application-method.store';
import { useTenantStore } from '@/store/tenant.store';

export class ApplicationMethodIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch application methods and update store
     */
    async fetchMethods(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useApplicationMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            // Map 'method' to 'name' for TableDeleteDialog compatibility
            const mappedData = (data || []).map(item => ({
                ...item,
                name: item.method
            }));
            store.setMethods(mappedData);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create application method
     */
    async createMethod(data) {
        const store = useApplicationMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create({
                ...data,
                tenantId
            });

            toast.success(i18next.t('applicationTrackingMethod:messages.createSuccess', { defaultValue: 'Application method created successfully' }));
            return { success: true, data: { ...response.data, name: response.data?.method } };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update application method
     */
    async updateMethod(id, data) {
        const store = useApplicationMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('applicationTrackingMethod:messages.updateSuccess', { defaultValue: 'Application method updated successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete application method
     */
    async deleteMethod(id) {
        const store = useApplicationMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.delete(id);

            toast.success(i18next.t('applicationTrackingMethod:messages.deleteSuccess', { defaultValue: 'Application method deleted successfully' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete application methods
     * @param {Array<string>} ids
     */
    async bulkDeleteMethods(ids) {
        const store = useApplicationMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('applicationTrackingMethod:messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: `${ids.length} application methods deleted successfully`,
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Application method not found',
        });
    }
}
